/**
 * Security view — sign-in activity, blocked addresses, sessions, user posture.
 *
 * Backs the admin side of the authentication hardening work
 * (documentation/security/auth_hardening_design.md). Five tabs:
 *
 *   activity   the `auth_attempts` log, filtered. Every sign-in attempt on
 *              every staff app, including the ones refused before a password
 *              was checked.
 *   addresses  failures grouped by address, with the accounts each one tried —
 *              the view that tells a typo from a spray.
 *   blocks     day-blocked addresses (released only here, with a reason) and
 *              the permanent allowlist for office/VPN ranges.
 *   sessions   live sessions across all apps, with a force-sign-out.
 *   posture    per-user second-factor and password age, so the gaps are visible.
 *
 * Everything is read-only except four actions, all of which the server audits:
 * release a block, add an entry, revoke a user's sessions, reset a user's
 * two-factor. The last two are reason-gated because resetting somebody's second
 * factor is the one socially-engineerable step in the whole design.
 */

import { escapeHtml, formatDateTimeFull, formatNumber, formatRelativeTime } from '../../src/lib/format.js';
import { closeDialog, openDialog } from '../../src/lib/dialog.js';
import * as adminAPI from '../api-client.js';
import * as notify from '../notifications.js';

const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

const PAGE_SIZE = 100;

const state = {
  tab: 'activity',
  attemptsOffset: 0,
  attemptsTotal: 0,
  posture: [],
  entryMode: 'allowlisted',
  reasonAction: null, // { verb, run(reason) }
};

let dom = null;

function $(id) { return document.getElementById(id); }

function resolveDom() {
  if (dom) return dom;
  dom = {
    view: $('securityView'),
    tabs: $('securityTabs'),
    enforcementNote: $('securityEnforcementNote'),
    kpi: {
      failed: $('secKpiFailed'),
      success: $('secKpiSuccess'),
      locked: $('secKpiLocked'),
      blocked: $('secKpiBlocked'),
      sessions: $('secKpiSessions'),
      mfa: $('secKpiMfa'),
    },
    filters: {
      outcome: $('secFilterOutcome'),
      population: $('secFilterPopulation'),
      identifier: $('secFilterIdentifier'),
      ip: $('secFilterIp'),
      hours: $('secFilterHours'),
    },
    attemptsBody: $('secAttemptsBody'),
    attemptsPageInfo: $('secAttemptsPageInfo'),
    byIpBody: $('secByIpBody'),
    blocksBody: $('secBlocksBody'),
    showReleased: $('secShowReleased'),
    sessionsBody: $('secSessionsBody'),
    postureBody: $('secPostureBody'),
    postureSearch: $('secPostureSearch'),
    entryDlg: $('secEntryDlg'),
    entryForm: $('secEntryForm'),
    entryTitle: $('secEntryTitle'),
    entryHelp: $('secEntryHelp'),
    entryValue: $('secEntryValue'),
    entryNote: $('secEntryNote'),
    entryError: $('secEntryError'),
    reasonDlg: $('secReasonDlg'),
    reasonForm: $('secReasonForm'),
    reasonTitle: $('secReasonTitle'),
    reasonBody: $('secReasonBody'),
    reasonInput: $('secReasonInput'),
    reasonError: $('secReasonError'),
  };
  return dom;
}

// ---- presentation helpers ------------------------------------------------

const OUTCOME_LABELS = {
  success: ['Signed in', 'ok'],
  bad_password: ['Wrong password', 'warn'],
  unknown_user: ['Unknown account', 'warn'],
  inactive: ['Deactivated', 'warn'],
  locked: ['Refused — cool-off', 'err'],
  ip_blocked: ['Refused — blocked', 'err'],
  mfa_required: ['Two-factor required', ''],
  mfa_failed: ['Two-factor failed', 'err'],
  password_expired: ['Password expired', 'warn'],
  forbidden: ['Wrong app for role', 'warn'],
  reset_requested: ['Reset requested', ''],
  reset_completed: ['Reset completed', 'ok'],
  reset_failed: ['Reset code refused', 'warn'],
};

const POPULATION_LABELS = {
  admin: 'Admin console',
  tech: 'Technician app',
  infra_tech: 'Infra-tech app',
  distributor: 'Distributor app',
};

function outcomeBadge(outcome) {
  const [label, tone] = OUTCOME_LABELS[outcome] || [outcome || '—', ''];
  return `<span class="badge ${tone}">${escapeHtml(label)}</span>`;
}

function shortDevice(ua) {
  if (!ua) return '—';
  // The full user-agent is noise in a table; the recognisable part is enough to
  // answer "is this the same browser I use?".
  const m = /(Chrome|Firefox|Safari|Edg|OkHttp|python-requests|curl)[\/ ]?([0-9]+)?/i.exec(ua);
  const platform = /(Windows|Android|iPhone|iPad|Mac OS X|Linux)/i.exec(ua);
  const parts = [m ? m[1] : null, platform ? platform[1] : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : ua.slice(0, 40);
}

function emptyRow(colspan, message) {
  return `<tr><td colspan="${colspan}" class="table-meta" style="text-align:center; padding:18px;">${escapeHtml(message)}</td></tr>`;
}

async function call(url, options) {
  try {
    return await adminAPI.fetch(url, options);
  } catch (err) {
    showFlash(err?.detail || err?.message || 'Request failed', 'error');
    throw err;
  }
}

// ---- KPI strip ----------------------------------------------------------

async function loadOverview() {
  const d = resolveDom();
  let data;
  try {
    data = await adminAPI.fetch(adminAPI.routes.securityOverview, { method: 'GET' });
  } catch {
    return;
  }
  d.kpi.failed.textContent = formatNumber(data.failed_24h || 0);
  d.kpi.success.textContent = formatNumber(data.success_24h || 0);
  d.kpi.locked.textContent = formatNumber(data.locked_now || 0);
  d.kpi.blocked.textContent = formatNumber(data.blocked_ips || 0);
  d.kpi.sessions.textContent = formatNumber(data.live_sessions || 0);
  d.kpi.mfa.textContent = formatNumber(data.mfa_enrolled_users || 0);

  const l = data.limits || {};
  const bits = [
    `${l.per_ip} failures per address or ${l.per_account} per account in ${l.window_minutes} min → ${l.cooloff_minutes} min cool-off`,
    `${l.day_block_threshold} failures per address in ${l.day_window_hours}h → blocked until released`,
    `${data.allowlisted_ranges || 0} allowlisted range(s)`,
  ];
  d.enforcementNote.innerHTML = data.enforcement
    ? escapeHtml(bits.join(' · '))
    : `<span class="badge err">Enforcement OFF</span> attempts are being logged but nothing is being blocked — ${escapeHtml(bits.join(' · '))}`;
}

// ---- activity -----------------------------------------------------------

function attemptsQuery() {
  const d = resolveDom();
  const params = new URLSearchParams();
  if (d.filters.outcome.value) params.set('outcome', d.filters.outcome.value);
  if (d.filters.population.value) params.set('population', d.filters.population.value);
  if (d.filters.identifier.value.trim()) params.set('identifier', d.filters.identifier.value.trim());
  if (d.filters.ip.value.trim()) params.set('ip', d.filters.ip.value.trim());
  params.set('hours', d.filters.hours.value || '24');
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(state.attemptsOffset));
  return params.toString();
}

async function loadAttempts() {
  const d = resolveDom();
  d.attemptsBody.innerHTML = emptyRow(6, 'Loading…');
  let data;
  try {
    data = await adminAPI.fetch(adminAPI.routes.securityAttempts(attemptsQuery()), { method: 'GET' });
  } catch {
    d.attemptsBody.innerHTML = emptyRow(6, 'Could not load the sign-in log.');
    return;
  }
  state.attemptsTotal = data.total || 0;
  const items = data.items || [];
  if (!items.length) {
    d.attemptsBody.innerHTML = emptyRow(6, 'No sign-in attempts match these filters.');
  } else {
    d.attemptsBody.innerHTML = items.map((r) => `
      <tr>
        <td title="${escapeHtml(formatDateTimeFull(r.occurred_at))}">${escapeHtml(formatRelativeTime(r.occurred_at))}</td>
        <td>${outcomeBadge(r.outcome)}</td>
        <td>${escapeHtml(r.identifier || '—')}</td>
        <td>${escapeHtml(POPULATION_LABELS[r.population] || r.population || '—')}</td>
        <td><code>${escapeHtml(r.ip || '—')}</code></td>
        <td title="${escapeHtml(r.user_agent || '')}">${escapeHtml(shortDevice(r.user_agent))}</td>
      </tr>`).join('');
  }
  const from = state.attemptsTotal ? state.attemptsOffset + 1 : 0;
  const to = Math.min(state.attemptsOffset + items.length, state.attemptsTotal);
  d.attemptsPageInfo.textContent = `${from}–${to} of ${formatNumber(state.attemptsTotal)}`;
}

// ---- addresses ----------------------------------------------------------

async function loadByIp() {
  const d = resolveDom();
  d.byIpBody.innerHTML = emptyRow(5, 'Loading…');
  const hours = d.filters.hours.value || '24';
  let rows;
  try {
    rows = await adminAPI.fetch(adminAPI.routes.securityAttemptsByIp(hours), { method: 'GET' });
  } catch {
    d.byIpBody.innerHTML = emptyRow(5, 'Could not load the address summary.');
    return;
  }
  if (!rows.length) {
    d.byIpBody.innerHTML = emptyRow(5, 'No failed sign-ins in this window.');
    return;
  }
  d.byIpBody.innerHTML = rows.map((r) => {
    let status = '<span class="badge">—</span>';
    if (r.allowlisted) status = '<span class="badge ok">Allowlisted</span>';
    else if (r.blocked) status = '<span class="badge err">Blocked</span>';
    const action = (r.allowlisted || r.blocked)
      ? ''
      : `<button class="btn xs" type="button" data-sec-block="${escapeHtml(r.ip_prefix)}">Block</button>
         <button class="btn xs ghost" type="button" data-sec-allow="${escapeHtml(r.ip_prefix)}">Allowlist</button>`;
    return `
      <tr>
        <td><code>${escapeHtml(r.ip_prefix)}</code></td>
        <td>${formatNumber(r.failures)}</td>
        <td class="table-meta">${escapeHtml((r.identifiers || []).join(', ') || '—')}</td>
        <td>${status}</td>
        <td style="white-space:nowrap;">${action}</td>
      </tr>`;
  }).join('');
}

// ---- blocks + allowlist -------------------------------------------------

async function loadBlocks() {
  const d = resolveDom();
  d.blocksBody.innerHTML = emptyRow(7, 'Loading…');
  const params = new URLSearchParams();
  if (d.showReleased.checked) params.set('include_released', 'true');
  let rows;
  try {
    rows = await adminAPI.fetch(adminAPI.routes.securityIpBlocks(params.toString()), { method: 'GET' });
  } catch {
    d.blocksBody.innerHTML = emptyRow(7, 'Could not load the address list.');
    return;
  }
  if (!rows.length) {
    d.blocksBody.innerHTML = emptyRow(7, 'Nothing blocked, and nothing allowlisted yet. Add your office and VPN ranges.');
    return;
  }
  d.blocksBody.innerHTML = rows.map((r) => {
    const released = Boolean(r.released_at);
    const badge = r.status === 'allowlisted'
      ? '<span class="badge ok">Allowlisted</span>'
      : (released ? '<span class="badge">Released</span>' : '<span class="badge err">Blocked</span>');
    const actions = [];
    if (r.status === 'blocked' && !released) {
      actions.push(`<button class="btn xs" type="button" data-sec-release="${r.id}" data-sec-entry="${escapeHtml(r.ip_or_cidr)}">Release</button>`);
      actions.push(`<button class="btn xs ghost" type="button" data-sec-allow="${escapeHtml(r.ip_or_cidr)}">Allowlist</button>`);
    }
    if (r.status === 'allowlisted') {
      actions.push(`<button class="btn xs warn" type="button" data-sec-delete="${r.id}" data-sec-entry="${escapeHtml(r.ip_or_cidr)}">Remove</button>`);
    }
    return `
      <tr>
        <td><code>${escapeHtml(r.ip_or_cidr)}</code></td>
        <td>${badge}</td>
        <td class="table-meta">${escapeHtml(r.reason || '—')}</td>
        <td>${r.fail_count_at_block != null ? formatNumber(r.fail_count_at_block) : '—'}</td>
        <td title="${escapeHtml(formatDateTimeFull(r.blocked_at))}">${escapeHtml(formatRelativeTime(r.blocked_at))}</td>
        <td class="table-meta">${escapeHtml(r.notes || '—')}</td>
        <td style="white-space:nowrap;">${actions.join(' ')}</td>
      </tr>`;
  }).join('');
}

function openEntryDialog(mode, prefill) {
  const d = resolveDom();
  state.entryMode = mode;
  d.entryTitle.textContent = mode === 'allowlisted' ? 'Allowlist a range' : 'Block an address';
  d.entryHelp.textContent = mode === 'allowlisted'
    ? 'Allowlisted ranges skip the cool-off and the day-block entirely. Use it for office and VPN addresses — staff behind one public address would otherwise lock each other out.'
    : 'Blocks the address from signing in until somebody releases it here.';
  d.entryValue.value = prefill || '';
  d.entryNote.value = '';
  d.entryError.style.display = 'none';
  openDialog(d.entryDlg);
}

async function submitEntry(event) {
  event.preventDefault();
  const d = resolveDom();
  const value = d.entryValue.value.trim();
  if (!value) {
    d.entryError.textContent = 'Give an address or CIDR range.';
    d.entryError.style.display = '';
    return;
  }
  try {
    await adminAPI.fetch(adminAPI.routes.securityIpBlocks(), {
      method: 'POST',
      body: JSON.stringify({
        ip_or_cidr: value,
        status: state.entryMode,
        notes: d.entryNote.value.trim() || undefined,
      }),
    });
  } catch (err) {
    d.entryError.textContent = err?.detail || err?.message || 'Could not save that entry.';
    d.entryError.style.display = '';
    return;
  }
  closeDialog(d.entryDlg);
  showFlash(state.entryMode === 'allowlisted' ? 'Range allowlisted' : 'Address blocked');
  await Promise.all([loadBlocks(), loadOverview(), loadByIp()]);
}

function askReason({ title, body, verb, run }) {
  const d = resolveDom();
  state.reasonAction = { run };
  d.reasonTitle.textContent = title;
  d.reasonBody.textContent = body;
  d.reasonInput.value = '';
  d.reasonError.style.display = 'none';
  $('secReasonSubmit').textContent = verb || 'Confirm';
  openDialog(d.reasonDlg);
}

async function submitReason(event) {
  event.preventDefault();
  const d = resolveDom();
  const reason = d.reasonInput.value.trim();
  if (!reason) {
    d.reasonError.textContent = 'A reason is required — it goes in the audit log.';
    d.reasonError.style.display = '';
    return;
  }
  const action = state.reasonAction;
  if (!action) { closeDialog(d.reasonDlg); return; }
  try {
    await action.run(reason);
  } catch (err) {
    d.reasonError.textContent = err?.detail || err?.message || 'Action failed.';
    d.reasonError.style.display = '';
    return;
  }
  closeDialog(d.reasonDlg);
  state.reasonAction = null;
}

// ---- sessions -----------------------------------------------------------

async function loadSessions() {
  const d = resolveDom();
  d.sessionsBody.innerHTML = emptyRow(8, 'Loading…');
  let rows;
  try {
    rows = await adminAPI.fetch(adminAPI.routes.securitySessions(), { method: 'GET' });
  } catch {
    d.sessionsBody.innerHTML = emptyRow(8, 'Could not load sessions.');
    return;
  }
  if (!rows.length) {
    d.sessionsBody.innerHTML = emptyRow(8, 'No live sessions.');
    return;
  }
  d.sessionsBody.innerHTML = rows.map((r) => `
    <tr>
      <td>${escapeHtml(r.username || r.admin_user_id)}</td>
      <td>${escapeHtml(POPULATION_LABELS[r.population] || r.population)}</td>
      <td title="${escapeHtml(formatDateTimeFull(r.issued_at))}">${escapeHtml(formatRelativeTime(r.issued_at))}</td>
      <td>${r.last_seen_at ? escapeHtml(formatRelativeTime(r.last_seen_at)) : '—'}</td>
      <td title="${escapeHtml(formatDateTimeFull(r.expires_at))}">${escapeHtml(formatRelativeTime(r.expires_at))}</td>
      <td><code>${escapeHtml(r.ip || '—')}</code></td>
      <td title="${escapeHtml(r.user_agent || '')}">${escapeHtml(shortDevice(r.user_agent))}</td>
      <td><button class="btn xs warn" type="button" data-sec-revoke="${r.admin_user_id}" data-sec-user="${escapeHtml(r.username || '')}">Sign out</button></td>
    </tr>`).join('');
}

// ---- posture ------------------------------------------------------------

async function loadPosture() {
  const d = resolveDom();
  d.postureBody.innerHTML = emptyRow(7, 'Loading…');
  try {
    state.posture = await adminAPI.fetch(adminAPI.routes.securityUsersPosture, { method: 'GET' });
  } catch {
    d.postureBody.innerHTML = emptyRow(7, 'Could not load user posture.');
    return;
  }
  renderPosture();
}

function renderPosture() {
  const d = resolveDom();
  const needle = (d.postureSearch.value || '').trim().toLowerCase();
  const rows = state.posture.filter((u) => !needle
    || (u.username || '').toLowerCase().includes(needle)
    || (u.email || '').toLowerCase().includes(needle));
  if (!rows.length) {
    d.postureBody.innerHTML = emptyRow(7, 'No users match.');
    return;
  }
  d.postureBody.innerHTML = rows.map((u) => {
    let mfa;
    if (u.mfa_enrolled) {
      const left = u.mfa_recovery_codes_left;
      mfa = `<span class="badge ok">On</span>${left <= 2 ? ` <span class="badge warn">${left} recovery codes left</span>` : ''}`;
    } else if (u.mfa_required) {
      mfa = '<span class="badge err">Required, not set up</span>';
    } else {
      mfa = '<span class="badge">Off</span>';
    }

    const days = u.password_expires_in_days;
    let age = '—';
    if (u.must_change_password) age = '<span class="badge warn">Change pending</span>';
    else if (days == null) age = '<span class="table-meta">no rotation</span>';
    else if (days < 0) age = `<span class="badge err">Expired ${Math.abs(days)}d ago</span>`;
    else if (days <= 7) age = `<span class="badge warn">${days}d left</span>`;
    else age = `<span class="table-meta">${days}d left</span>`;

    return `
      <tr>
        <td>${escapeHtml(u.username)}<div class="table-meta">${escapeHtml(u.email || '')}</div></td>
        <td class="table-meta">${escapeHtml((u.roles || []).join(', ') || '—')}</td>
        <td>${mfa}</td>
        <td>${age}</td>
        <td>${u.last_login ? escapeHtml(formatRelativeTime(u.last_login)) : '—'}</td>
        <td>${u.is_active ? '<span class="badge ok">Active</span>' : '<span class="badge">Deactivated</span>'}</td>
        <td style="white-space:nowrap;">
          <button class="btn xs" type="button" data-sec-force-change="${u.id}" data-sec-user="${escapeHtml(u.username)}">Force change</button>
          <button class="btn xs ghost" type="button" data-sec-revoke="${u.id}" data-sec-user="${escapeHtml(u.username)}">Sign out</button>
          ${u.mfa_enrolled ? `<button class="btn xs warn" type="button" data-sec-reset-mfa="${u.id}" data-sec-user="${escapeHtml(u.username)}">Reset 2FA</button>` : ''}
        </td>
      </tr>`;
  }).join('');
}

// ---- tabs ---------------------------------------------------------------

function showTab(name) {
  const d = resolveDom();
  state.tab = name;
  d.tabs.querySelectorAll('button[data-sec-tab]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.secTab === name);
  });
  d.view.querySelectorAll('[data-sec-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.secPanel !== name;
  });
  if (name === 'activity') loadAttempts();
  if (name === 'addresses') loadByIp();
  if (name === 'blocks') loadBlocks();
  if (name === 'sessions') loadSessions();
  if (name === 'posture') loadPosture();
}

// ---- wiring -------------------------------------------------------------

let mounted = false;

function mount() {
  if (mounted) return;
  const d = resolveDom();
  if (!d.view) return;
  mounted = true;

  d.tabs.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-sec-tab]');
    if (btn) showTab(btn.dataset.secTab);
  });

  // Activity filters: re-query from the first page.
  const refilter = () => { state.attemptsOffset = 0; loadAttempts(); };
  ['outcome', 'population', 'hours'].forEach((key) => {
    d.filters[key].addEventListener('change', () => {
      refilter();
      if (state.tab === 'addresses') loadByIp();
    });
  });
  let debounce = null;
  ['identifier', 'ip'].forEach((key) => {
    d.filters[key].addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(refilter, 350);
    });
  });
  $('secAttemptsRefresh').addEventListener('click', () => { loadAttempts(); loadOverview(); });
  $('secAttemptsPrev').addEventListener('click', () => {
    if (state.attemptsOffset <= 0) return;
    state.attemptsOffset = Math.max(0, state.attemptsOffset - PAGE_SIZE);
    loadAttempts();
  });
  $('secAttemptsNext').addEventListener('click', () => {
    if (state.attemptsOffset + PAGE_SIZE >= state.attemptsTotal) return;
    state.attemptsOffset += PAGE_SIZE;
    loadAttempts();
  });

  $('secBlocksRefresh').addEventListener('click', () => { loadBlocks(); loadOverview(); });
  d.showReleased.addEventListener('change', loadBlocks);
  $('secAddAllowBtn').addEventListener('click', () => openEntryDialog('allowlisted'));
  $('secAddBlockBtn').addEventListener('click', () => openEntryDialog('blocked'));
  d.entryForm.addEventListener('submit', submitEntry);
  d.reasonForm.addEventListener('submit', submitReason);

  $('secSessionsRefresh').addEventListener('click', () => { loadSessions(); loadOverview(); });
  $('secPostureRefresh').addEventListener('click', loadPosture);
  d.postureSearch.addEventListener('input', renderPosture);

  // Row actions, delegated across every panel.
  d.view.addEventListener('click', async (e) => {
    const release = e.target.closest('[data-sec-release]');
    if (release) {
      const entry = release.dataset.secEntry;
      askReason({
        title: `Release ${entry}`,
        body: 'Sign-in from this address will be allowed again. If it is a colleague, consider allowlisting their range as well so it does not happen again.',
        verb: 'Release',
        run: async (reason) => {
          await call(adminAPI.routes.securityIpBlockRelease(release.dataset.secRelease), {
            method: 'POST', body: JSON.stringify({ reason }),
          });
          showFlash(`Released ${entry}`);
          await Promise.all([loadBlocks(), loadOverview()]);
        },
      });
      return;
    }
    const del = e.target.closest('[data-sec-delete]');
    if (del) {
      await call(adminAPI.routes.securityIpBlockDelete(del.dataset.secDelete), { method: 'DELETE' });
      showFlash(`Removed ${del.dataset.secEntry}`);
      await Promise.all([loadBlocks(), loadOverview()]);
      return;
    }
    const allow = e.target.closest('[data-sec-allow]');
    if (allow) { openEntryDialog('allowlisted', allow.dataset.secAllow); return; }
    const block = e.target.closest('[data-sec-block]');
    if (block) { openEntryDialog('blocked', block.dataset.secBlock); return; }

    const revoke = e.target.closest('[data-sec-revoke]');
    if (revoke) {
      askReason({
        title: `Sign ${revoke.dataset.secUser || 'this user'} out everywhere`,
        body: 'Every session for this user, in every app, ends immediately. They can sign in again unless you also deactivate the account.',
        verb: 'Sign out',
        run: async (reason) => {
          const res = await call(adminAPI.routes.securityRevokeUserSessions(revoke.dataset.secRevoke), {
            method: 'POST', body: JSON.stringify({ reason }),
          });
          showFlash(`Ended ${res.count} session(s)`);
          await Promise.all([loadSessions(), loadOverview()]);
        },
      });
      return;
    }
    const force = e.target.closest('[data-sec-force-change]');
    if (force) {
      await call(adminAPI.routes.securityRequirePasswordChange(force.dataset.secForceChange), { method: 'POST' });
      showFlash(`${force.dataset.secUser} must change their password at next sign-in`);
      await loadPosture();
      return;
    }
    const resetMfa = e.target.closest('[data-sec-reset-mfa]');
    if (resetMfa) {
      askReason({
        title: `Reset two-factor for ${resetMfa.dataset.secUser}`,
        body: 'This removes their authenticator enrolment and all recovery codes, so the next sign-in only needs their password until they re-enrol. Confirm you have verified who you are talking to — this is the step an attacker would try to talk you into.',
        verb: 'Reset',
        run: async (reason) => {
          await call(adminAPI.routes.securityResetMfa(resetMfa.dataset.secResetMfa), {
            method: 'POST', body: JSON.stringify({ reason }),
          });
          showFlash('Two-factor reset');
          await Promise.all([loadPosture(), loadOverview()]);
        },
      });
    }
  });
}

function load() {
  mount();
  loadOverview();
  showTab(state.tab);
}

export default { mount, init: load, load };
